// ResortReservations.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Spinner } from "react-bootstrap";

import AddReservation from "./AddReservation";
import BookingReport from "./BookingReport";
import ReservationFilters from "./ReservationFilters";
import ReservationStats from "./ReservationStats";
import ReservationTable from "./ReservationTable";
import ReservationExportButton from "./ReservationExportButton";
import { deleteContent, getContents, getRoomPlans } from "../../../FIREBASECONFIG";

import { getUserRole } from "../../../APIS";

const getCurrentWeekRange = () => ({
  startDate: moment().startOf("isoWeek").format("YYYY-MM-DD"),
  endDate: moment().endOf("isoWeek").format("YYYY-MM-DD"),
});

const ResortReservations = () => {
  const collectionName = "reservations";
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  const userHotelId = userDetails.hotelId || "";
  const hotelDropdownLocked = Boolean(userHotelId);
  const role = getUserRole();

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const { startDate, endDate } = getCurrentWeekRange();
    return {
      searchTerm: "",
      status: "All",
      startDate,
      endDate,
      hotelId: "",
      roomTypeId: "",
      roomPlanId: "",
    };
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editReservation, setEditReservation] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [roomPlanOptions, setRoomPlanOptions] = useState([]);
  const tableRef = useRef(null);

  /** 🔹 Fetch Reservations */
  const fetchReservations = async () => {
    try {
      setLoading(true);
      const data = await getContents(collectionName);
      setReservations(data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  /** 🔹 Fetch Hotels */
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        let Hotels = await getContents("hotels");
        if (hotelDropdownLocked) {
          const match = Hotels.filter((h) => h.id === userHotelId);
          Hotels = match.length
            ? match
            : [{ id: userHotelId, name: userHotelId }];
          setFilters((prev) => ({ ...prev, hotelId: userHotelId }));
        }

        setHotelOptions(
          Hotels.map((h) => ({ label: h.name, value: h.id, rawData: h }))
        );
      } catch (err) {
        console.error("Hotel fetch error:", err);
      }
    };
    fetchHotels();
  }, [hotelDropdownLocked, userHotelId]);

  const selectedHotelId = userHotelId || filters.hotelId;

  /** 🔹 Fetch room types & plans when a hotel is selected */
  useEffect(() => {
    const fetchRoomFilterOptions = async () => {
      if (!selectedHotelId) {
        setRoomTypeOptions([]);
        setRoomPlanOptions([]);
        return;
      }

      try {
        const allRooms = await getContents("hotel_rooms");
        const hotelRooms = allRooms.filter((r) => r.hotel_id === selectedHotelId);

        setRoomTypeOptions(
          hotelRooms.map((r) => ({ label: r.name, value: r.id }))
        );

        const plansByRoom = await Promise.all(
          hotelRooms.map(async (room) => {
            const plans = await getRoomPlans(room.id);
            return plans.map((plan) => ({
              label: plan.name || plan.planName,
              value: plan.id,
              roomId: room.id,
            }));
          })
        );

        setRoomPlanOptions(plansByRoom.flat());
      } catch (err) {
        console.error("Room filter options fetch error:", err);
        setRoomTypeOptions([]);
        setRoomPlanOptions([]);
      }
    };

    fetchRoomFilterOptions();
  }, [selectedHotelId]);

  /** 🔹 Filtered Reservations */

  const filteredReservations = useMemo(() => {
    const { status, startDate, endDate, hotelId, roomTypeId, roomPlanId } =
      filters;

    const filterData = reservations.filter((r) => {
      const checkIn = new Date(r.checkIn);
      // console.log("before:", reservations);
      // 🔹 Date Filter

      let matchDate = true;

      switch (true) {
        // 👉 Case 1: Date range filter applied
        case !!startDate && !!endDate:
          matchDate =
            moment(checkIn, "YYYY-MM-DD").isSameOrAfter(moment(startDate)) &&
            moment(checkIn, "YYYY-MM-DD").isSameOrBefore(moment(endDate));
          break;

        // 👉 Case 2: Only start date (single date search)
        case !!startDate:
          matchDate = moment(checkIn, "YYYY-MM-DD").isSameOrAfter(
            moment(startDate)
          );
          break;

        // 👉 Default: show all reservations unless the user picks a date filter
        default:
          matchDate = true;
      }

      // // 🔹 Search Filter
      // const matchSearch =
      //   (r.bookingId || "").toLowerCase().includes(search) ||
      //   (r.firstName || "").toLowerCase().includes(search) ||
      //   (r.booker_email || "").toLowerCase().includes(search) ||
      //   (r.status || "").toLowerCase().includes(search);

      // 🔹 Status Filter
      const matchStatus =
        status === "All" ||
        (r.status || "").toLowerCase() === status.toLowerCase();

      // 🔹 Hotel Filter (scoped users: only their hotel; admins: optional filter)
      let matchHotel = true;
      if (userHotelId) {
        matchHotel = r.hotelId === userHotelId;
      } else if (hotelId) {
        matchHotel = r.hotelId === hotelId;
      }

      const matchRoomType =
        !roomTypeId ||
        (r.roomDetails || []).some((detail) => detail.id === roomTypeId);

      const matchRoomPlan =
        !roomPlanId ||
        (r.roomDetails || []).some((detail) => detail.planId === roomPlanId);

      return matchStatus && matchDate && matchHotel && matchRoomType && matchRoomPlan;
    });
    // console.log("after:", filterData);
    return filterData;
  }, [
    reservations,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.hotelId,
    filters.roomTypeId,
    filters.roomPlanId,
    userHotelId,
  ]);

  /** 🔹 Actions */
  const handleDelete = async (id) => {
    try {
      await deleteContent(collectionName, id);
      setReservations((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("Error deleting reservation:", error);
    }
  };

  const handleEdit = (item) => {
    setEditReservation(item);
    setShowForm(true);
  };

  const toggleForm = async () => {
    if (showForm) await fetchReservations();
    setShowForm((prev) => !prev);
    setEditReservation(null);
  };

  return (
    <div className="col-12">
      {showFilters && !showForm && (
        <div className="card mb-3">
          <div className="card-body border-bottom">
            <ReservationFilters
              filters={filters}
              setFilters={setFilters}
              hotelOptions={hotelOptions}
              disableHotelSelect={hotelDropdownLocked}
              userHotelId={userHotelId}
              getDefaultDateRange={getCurrentWeekRange}
              selectedHotelId={selectedHotelId}
              roomTypeOptions={roomTypeOptions}
              roomPlanOptions={roomPlanOptions}
            />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="mb-0">Hotel Reservations</h4>
          <div className="d-flex align-items-center gap-2">
            {!showForm && (
              <>
                <button
                  type="button"
                  className={`btn ${showFilters ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setShowFilters((prev) => !prev)}
                >
                  <i className="fa-solid fa-filter me-2" />
                  Filters
                </button>
                <ReservationExportButton
                  tableRef={tableRef}
                  reservations={filteredReservations}
                  disabled={loading}
                />
              </>
            )}
            <button className="btn btn-secondary" onClick={toggleForm}>
              {!showForm && <i className="fa-solid fa-plus me-2" />}
              {showForm ? "Close" : ""}
            </button>
          </div>
        </div>
        {/* <ReservationStats reservations={reservations} /> */}
        <ReservationStats reservations={filteredReservations} />

        {/* Body */}
        <div className="card-body">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <Spinner animation="border" />
            </div>
          ) : showForm ? (
            <AddReservation
              role={role}
              userDetails={userDetails}
              reservation={editReservation}
              toggleForm={toggleForm}
            />
          ) : (
            <ReservationTable
              ref={tableRef}
              reservations={filteredReservations}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              setSelectedReservation={setSelectedReservation}
            />
          )}
        </div>
      </div>

      {/* Report Modal */}
      {selectedReservation && (
        <BookingReport
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
        />
      )}
    </div>
  );
};

export default ResortReservations;
